import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ClinicReminderSettingsForm from './ClinicReminderSettingsForm';
import Loading from '../../../../common/Loading';
import {
    PREFERENCE_FIELDS,
    PREFERENCE_LABELS,
    countEnabledReminders,
    getPreferenceText,
    getReminderEntries,
    getReminderIconClass,
    getReminderIconTone,
    getReminderLabel,
} from './clinicReminderUtils';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const ReminderRow = ({ reminderKey, enabled }) => (
    <div className="clinic-reminder-row">
        <span className="clinic-reminder-row__label">
            <span className={`clinic-reminder-row__icon clinic-reminder-row__icon--${getReminderIconTone(reminderKey)}`}>
                <i className={`bi ${getReminderIconClass(reminderKey)}`}></i>
            </span>
            {getReminderLabel(reminderKey)}
        </span>
        <span className={`clinic-reminder-status-badge ${enabled ? 'clinic-reminder-status-badge--enabled' : 'clinic-reminder-status-badge--disabled'}`}>
            <i className={`bi ${enabled ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
            {enabled ? 'Enabled' : 'Disabled'}
        </span>
    </div>
);

function ReminderTab() {
    const [reminderSettings, setReminderSettings] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        loadSettings();
    }, [clinicId]);

    const loadSettings = () => {
        setIsLoading(true);
        setIsError(false);
        clinicService
            .getReminderSettings(clinicId)
            .then((res) => {
                setReminderSettings(res.data);
                setLastModified(res.data.modifiedAt);
            })
            .catch((err) => {
                setIsError(true);
                setErrorMessage(err.response?.data?.message || 'Failed to load reminder settings.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const updateReminderSettings = (settings) => {
        setReminderSettings(settings);
        setLastModified(settings.modifiedAt);
        setIsEditing(false);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const counts = countEnabledReminders(reminderSettings);
    const reminderGroups = getReminderEntries(reminderSettings);

    if (isLoading) {
        return <Loading />;
    }

    if (isEditing) {
        return (
            <ClinicReminderSettingsForm
                reminderSettings={reminderSettings}
                updateSettings={updateReminderSettings}
                onCancel={() => setIsEditing(false)}
            />
        );
    }

    return (
        <div className="row g-3">
            <div className="col-lg-8 clinic-reminder-panel">
                <div className="d-flex align-items-end justify-content-between gap-2 mb-3 flex-wrap">
                    <div>
                        <h5 className="clinic-reminder__title mb-1">
                            <i className="bi bi-bell text-primary me-1"></i>
                            Clinic Reminder Settings
                        </h5>
                        <p className="text-muted small mb-0">Enable or disable reminder types and delivery channels for this clinic.</p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark border font-monospace small">ID: {clinicId}</span>
                        <ActionTooltipButton
                            tooltip={copied ? 'Copied!' : 'Copy clinic ID'}
                            tooltipId="copy-clinic-reminder-id"
                            variant="outline-secondary"
                            size="sm"
                            onClick={copyToClipboard}
                        >
                            <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                        </ActionTooltipButton>
                    </div>
                </div>

                <div className="notifications-panel">
                    <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders"></i>
                            Reminder Toggles
                        </span>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setIsEditing(true)}>
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                        </button>
                    </div>

                    <div className="notifications-panel__body">
                        {isError && (
                            <div className="alert alert-warning border-0 py-2 px-3 small mb-3">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {errorMessage}
                            </div>
                        )}

                        <div className="clinic-reminder-toolbar">
                            <div className="d-flex flex-wrap gap-2">
                                <span className="clinic-reminder-stat clinic-reminder-stat--total">{counts.total} reminders</span>
                                <span className="clinic-reminder-stat clinic-reminder-stat--enabled">{counts.enabled} enabled</span>
                                <span className="clinic-reminder-stat clinic-reminder-stat--disabled">{counts.disabled} disabled</span>
                            </div>
                            {lastModified && (
                                <span className="clinic-reminder-meta">
                                    Last modified: {lastModified}
                                </span>
                            )}
                        </div>

                        {reminderGroups.map((group, index) => (
                            <div key={group.title}>
                                <div className={`clinic-reminder-section-title${index === 0 ? ' clinic-reminder-section-title--first' : ''}`}>
                                    {group.title}
                                </div>
                                {group.items.map((item) => (
                                    <ReminderRow key={item.key} reminderKey={item.key} enabled={item.value === true} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="col-lg-4 clinic-reminder-preferences-panel">
                <div className="mb-3">
                    <h5 className="clinic-reminder__title mb-1">
                        <i className="bi bi-clock-history text-primary me-1"></i>
                        Reminder Preferences
                    </h5>
                    <p className="text-muted small mb-0">Timing, call routing, and summary delivery preferences.</p>
                </div>

                <div className="notifications-panel">
                    <div className="notifications-panel__header">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders"></i>
                            Preferences
                        </span>
                    </div>
                    <div className="notifications-panel__body">
                        {PREFERENCE_FIELDS.map((key, index) => (
                            <div key={key} className={`notifications-info-row${index === PREFERENCE_FIELDS.length - 1 ? ' mb-0' : ''}`}>
                                <span className="notifications-info-label">{PREFERENCE_LABELS[key]}</span>
                                <span className="notifications-info-value">
                                    {getPreferenceText(key, reminderSettings[key])}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReminderTab;
